import {State} from "@lit-app/state";
import {property} from "@lit-app/state/src/decorators/property.js";
import {storage} from "@lit-app/state/src/decorators/storage.js";
import {Router} from "@lit-labs/router";
import {html} from "lit";
import {DateTime} from "luxon";
import {distinctUntilChanged, filter, map, Observable, ReplaySubject, share} from "rxjs";
import {container} from "tsyringe";
import {UserInfo} from "./authentication/user.interface";
import type {FiltersDTO} from "./domain/dtos/filters.dto";
import type {KnowledgeFiltersDTO} from "./domain/dtos/knowledge-filters.dto";
import {Employee} from "./domain/entities/employee.entity";
import {User} from "./domain/entities/user.entity";
import {ObjectHelper} from "./helpers/object.helper";
import {UiHelper} from "./helpers/ui.helper";
import {AbstractHaTimelineView} from "./views/abstract-ha-timeline.view";
import {StringHelper} from "./helpers/string.helper";
import {migrateLocalStorageToSid} from "./helpers/local-storage-sid-migration.helper";
import {migrateSidebarNavigationState} from "./helpers/sidebar-navigation-state-migration.helper";
import {BOARD_SORT_STORAGE} from "./helpers/board-sort.helper";
import {StateEvent} from "@lit-app/state/src/state-event";

type SerializedTimelineConfiguration = {
    key: string,
    uids: string[]
}

type SelectedTimelineEntity = {
    uid: string,
    viewName: string
}

/**
 * The three cell-width views of the timeline toolbar (Anke, 2026-10-14): "Kompakt Ansicht",
 * "Standard Ansicht", "Detail Ansicht". Each button sets its view; the active one is marked.
 */
export type CellWidthView = 'compact' | 'standard' | 'detail';

/** Cell-width multiplier per view; the date columns are `40px × multiplier` (spa.css). */
export const CELL_WIDTH_MULTIPLIERS: Readonly<Record<CellWidthView, number>> = {
    compact: 0.5,
    standard: 1,
    detail: 4,
};

export type StickyEntity = {
    uid: string;
    baseEntity: string; // Klassenname z.B. "Project", "Employee" 
    timelineConfig: {
        pathToUid: string;
        pathToTitle: string;
        pathToEntity: string;
        baseEntityAlias?: string;
    };
    isSystemSticky?: boolean; // System-Sticky (immer sticky, kein Pin-Icon) vs User-Sticky
}

class AppState extends State {

    @storage({key: `cell-width`, prefix: `ha`})
    @property({type: Number, value: 1})
    public cellWidthMultiplication!: number;

    @storage({key: `date-cursor`, prefix: `ha`})
    @property({type: Number, value: DateTime.utc().startOf(`day`).toUnixInteger()})
    public dateCursor!: number

    @storage({key: `filters`, prefix: `ha`})
    @property({type: Object, value: {}})
    public filters!: FiltersDTO;

    @storage({key: `redirect`, prefix: `ha`})
    @property({type: String, value: null})
    public redirectAfterLogin!: string | null;

    /**
     * This browser's sort choice for the project board, `null` = follow the tenant default.
     * Raw on purpose (`string | null`): an old or hand-edited value, and the `"null"` string
     * `@storage` writes back on every start with an empty store, all reach here. Always read it
     * through `parseBoardSort()` / `effectiveBoardSort()` (helpers/board-sort.helper.ts).
     * No field initialiser, same as `redirectAfterLogin`.
     */
    @storage(BOARD_SORT_STORAGE)
    @property({type: String, value: null})
    public boardSort!: string | null;

    public router!: Router;

    /**
     * Persisted scroll anchor per tasklist mode (`/tasklist` vs `/absence-list`,
     * which share one component instance — a single scalar would let scrolling
     * one mode clobber the other's remembered position). Value is the anchored
     * issue's uid, or `null` meaning "today" (see HaTasklistView.scrollToToday()
     * and _restoreScrollAnchor()'s fallback). Renamed from the old
     * `taskListScrollPosition: number` (localStorage key `tasklist-scroll-position`)
     * because that key never actually persisted anything — the old listener was
     * wired to the wrong DOM element and never fired; the old key is left
     * orphaned in existing browsers' localStorage rather than migrated.
     */
    @storage({key: `tasklist-scroll-anchor`, prefix: `ha`})
    @property({type: Object, value: {tasks: null, absences: null}})
    public taskListScrollAnchor!: Record<'tasks' | 'absences', string | null>;

    @storage({key: `tasklist-view`, prefix: `ha`})
    @property({type: String, value: 'list-view'})
    public tasklistView!: 'list-view' | 'column-view';

    @storage({key: `user`, prefix: `ha`})
    @property({type: Object, value: null})
    public user!: UserInfo | null;

    @storage({key: `privileges`, prefix: `ha`})
    @property({type: Array, value: []})
    public privileges!: string[];

    @storage({key: `selectedTimelineEntities`, prefix: `ha`})
    @property({type: Array, value: []})
    public selectedTimelineEntities!: SelectedTimelineEntity[];

    @storage({key: `stickyTimelineEntities`, prefix: `ha`})
    @property({type: Array, value: []})
    public stickyTimelineEntities!: StickyEntity[];

    /**
     * Persisted Y-axis start cursors for timeline views.
     * Key: View name (e.g., 'boards', 'dailys'), Value: ShortId or null.
     * Restored on page reload to maintain scroll position across sessions.
     */
    @storage({key: `timelineViewCursors`, prefix: `ha`})
    @property({type: Object, value: {}})
    public timelineViewCursors!: Record<string, string | null>;

    @storage({key: `todoFilterOpen`, prefix: `ha`})
    @property({type: Boolean, value: false})
    public todoFilterOpen!: boolean;

    @storage({key: `knowledgeFilters`, prefix: `ha`})
    @property({type: Object, value: {}})
    public knowledgeFilters!: KnowledgeFiltersDTO;

    /** Open/closed state of the Knowledge `.sidebar__filter` drawer — mirrors
     *  `todoFilterOpen` 1:1 (same persisted-boolean pattern, same drawer CSS). */
    @storage({key: `knowledgeFilterOpen`, prefix: `ha`})
    @property({type: Boolean, value: false})
    public knowledgeFilterOpen!: boolean;

    // Mode-scoped filter slot for the resource-management mode (O2D-PRD-100).
    // Isolated from the ToDo `filters` slot so filtering resources never mutates
    // the ToDo view's filters. Reuses `FiltersDTO` verbatim (identical shape).
    @storage({key: `resourceFilters`, prefix: `ha`})
    @property({type: Object, value: {}})
    public resourceFilters!: FiltersDTO;

    /** ShortId of the KnowledgeObject the user opened last — used to deep-link
     * back to it when the user re-enters the knowledge mode at `/knowledge`. */
    @storage({key: `lastKnowledgeObjectSid`, prefix: `ha`})
    @property({type: String, value: null})
    public lastKnowledgeObjectSid!: string | null;

    @storage({key: `sidebarNavigationState`, prefix: `ha`})
    @property({type: String, value: null})
    public sidebarNavigationState!: 'expanded' | 'collapsed' | null;

    /** Manually dragged sidebar width in px (ha-app.component.ts's resize
     *  handle, review 01, 2026-08-17: "sollte auch gemerkt werden"), or
     *  `null` before the user has ever dragged it — falls back to the CSS
     *  `fit-content` default. */
    @storage({key: `sidebarWidth`, prefix: `ha`})
    @property({type: Number, value: null})
    public sidebarWidth!: number | null;

    // URL-derived mode state (TD-002 F6). Sole writer is ModeRouterController.
    // @property() is required for StateController-based reactivity; @storage() must NOT be added.
    @property({type: String, value: 'todo'})
    public currentMode!: 'todo' | 'wissen' | 'resource' | 'linkview' | 'personal-organizer';

    private _subject$ = new ReplaySubject<{ key: string, value: any }>();
    private _withoutInformListeners = false;
    private _tokenExpiryTimeout: ReturnType<typeof setTimeout> | null = null;
    private _isHandlingExpiry = false;

    private static readonly FALLBACK_EXPIRY_MS = 60_000;
    private static readonly NOTIFICATION_DURATION = 10_000;

    public constructor() {
        super();
        this.subscribe((key, value) => this._subject$.next({key, value}));

        // Auto-start/stop token expiry timer when user changes
        this.listen$('user').subscribe((user: UserInfo | null) => {
            if (user) {
                this.startTokenExpiryCheck();
            } else {
                this.stopTokenExpiryCheck();
            }
        });

        // Listen for session-expired events from BackendClient (avoids circular import)
        window.addEventListener('o2d.session-expired', () => this._handleSessionExpired());
    }

    /**
     * Starts a single setTimeout that fires when the current token expires.
     * Falls back to 60s if validUntil is not parseable.
     */
    public startTokenExpiryCheck(): void {
        // Re-arm the idempotent expiry guard so a fresh (re-)login session can
        // handle a future expiry again — the flag is never reset elsewhere.
        this._isHandlingExpiry = false;
        this.stopTokenExpiryCheck();

        const validUntil = this.user?.token?.validUntil;
        let delayMs: number;

        if (validUntil) {
            const expiryTime = DateTime.fromISO(validUntil);
            delayMs = expiryTime.isValid
                ? Math.max(0, expiryTime.diffNow().milliseconds)
                : AppState.FALLBACK_EXPIRY_MS;
        } else {
            delayMs = AppState.FALLBACK_EXPIRY_MS;
        }

        this._tokenExpiryTimeout = setTimeout(() => this._handleSessionExpired(), delayMs);
    }

    /** Clears any pending token expiry timeout. */
    public stopTokenExpiryCheck(): void {
        if (this._tokenExpiryTimeout !== null) {
            clearTimeout(this._tokenExpiryTimeout);
            this._tokenExpiryTimeout = null;
        }
    }

    /**
     * Handles session expiry: clears user and notifies. The auth-gate in
     * ha-app.render() reacts to clearUser() (user becomes null) and swaps the
     * router outlet for ha-login-view in-place — no navigation needed.
     * Idempotent — multiple calls are no-ops after the first.
     */
    public _handleSessionExpired(): void {
        if (this._isHandlingExpiry) return;
        this._isHandlingExpiry = true;

        this.stopTokenExpiryCheck();
        this.clearUser();

        UiHelper.notify({
            level: 'info',
            title: 'Sitzung abgelaufen',
            content: html`<p>Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.</p>`,
            closable: true,
            duration: AppState.NOTIFICATION_DURATION
        });
    }

    public get dateCursorDate(): DateTime {
        return DateTime.fromSeconds(this.dateCursor, {zone: `utc`})
    }

    public set dateCursorDate(value) {
        this.dateCursor = value.toUnixInteger();
    }

    public get employee(): Promise<Employee | null> {
        const username = this.user?.username ?? null;
        if (!username) {
            return Promise.resolve(null);
        }

        // @ts-ignore
        return container
            .resolve(`UserRepository`)
            .findOneBy(`username`, username)
            .then((user: User | null) => user ? user.employee : null);
    }

    public get isUserLoggedIn(): boolean {

        if (!this.user || !this.user.token?.validUntil) {
            return false;
        }

        return DateTime.fromISO(this.user.token.validUntil) >= DateTime.utc();
    }

    public get numberOfAppliedFilters(): number {
        return Object.entries(this.filters).length;
    }

    // Slot-aware applied-filters count for the resource-management mode (O2D-PRD-100).
    // Mirrors the same null/empty semantics `numberOfAppliedKnowledgeFilters`
    // (removed 2026-08-17, review 01 — its only caller, the toolbar reset
    // button's badge, was removed the same session) used — NOT the looser
    // `numberOfAppliedFilters` which counts every entry regardless of value.
    public get numberOfAppliedResourceFilters(): number {
        return Object.entries(this.resourceFilters ?? {})
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .length;
    }

    public clearUser(): void {
        this.privileges = [];
        this.user = null;
    }

    public hasAppliedFilters() {
        return this.numberOfAppliedFilters !== 0;
    }

    // Resource-slot variant of the applied-filters check (O2D-PRD-100).
    public hasAppliedResourceFilters() {
        return this.numberOfAppliedResourceFilters !== 0;
    }

    /**
     * The view the stored multiplier belongs to. Read by range, not by exact value, so an older
     * stored detail multiplier (5, 6) still counts as detail and anything unreadable as standard.
     */
    public cellWidthView(): CellWidthView {
        const multiplier = this.cellWidthMultiplication;
        if (multiplier > 1) {
            return 'detail';
        }
        return multiplier < 1 ? 'compact' : 'standard';
    }

    /**
     * True only in the detail view (wide cells). The compact view is NOT the detail view, so
     * "jump to today" keeps its normal offset there and the old timeline shows no titles.
     */
    public isWeekView() {
        return this.cellWidthView() === 'detail';
    }

    public listen$(keyListenFor: string): Observable<any> {
        return this._subject$.pipe(
            filter(({key}) => key === keyListenFor && !this._withoutInformListeners),
            distinctUntilChanged(
                (previous, current): boolean => ObjectHelper.deepEqual(previous, current)
            ),
            map(({value}) => value),
            share()
        );
    }

    /**
     * Sets the cell-width view (the three toolbar buttons). Setting the active view again changes
     * nothing — `@property` only notifies on a real change.
     */
    public setCellWidthView(view: CellWidthView): void {
        this.cellWidthMultiplication = CELL_WIDTH_MULTIPLIERS[view];
    }

    public withoutInformListeners(fn: () => void) {
        this._withoutInformListeners = true;
        fn();
        this._withoutInformListeners = false;
    }

    public addTimelineEntityToUserView(view: AbstractHaTimelineView, uid: string): void {
        const viewName = StringHelper.toKebabCase(view.constructor.name);

        if (this.selectedTimelineEntities.some(selectedTimelineEntity => selectedTimelineEntity.viewName === viewName && selectedTimelineEntity.uid === uid)) {
            return;
        }

        this.selectedTimelineEntities = [...this.selectedTimelineEntities, {uid, viewName}];
        this.dispatchEvent(new StateEvent(`selectedTimelineEntities`, this.selectedTimelineEntities, this));
    }

    public removeTimelineEntityFromUserView(view: AbstractHaTimelineView, uid: string): void {
        const viewName = StringHelper.toKebabCase(view.constructor.name);
        this.selectedTimelineEntities = this.selectedTimelineEntities.filter(entry => entry.uid !== uid && entry.viewName !== viewName);
        this.dispatchEvent(new StateEvent(`selectedTimelineEntities`, this.selectedTimelineEntities, this));
    }

    public isEntityPinnedToUserView(view: AbstractHaTimelineView, uid: string): boolean {
        const viewName = StringHelper.toKebabCase(view.constructor.name);
        return this.selectedTimelineEntities.some(entry => entry.uid === uid && (entry.viewName === viewName || viewName === 'ha-user-view'));
    }

    public addStickyTimelineEntity(baseEntityName: string, uid: string, timelineConfig: {
        pathToUid: string,
        pathToTitle: string,
        pathToEntity: string,
        baseEntityAlias?: string
    }): void {
        this._addStickyTimelineEntity(baseEntityName, uid, timelineConfig, false);
    }

    public addSystemStickyTimelineEntity(baseEntityName: string, uid: string, timelineConfig: {
        pathToUid: string,
        pathToTitle: string,
        pathToEntity: string,
        baseEntityAlias?: string
    }): void {
        this._addStickyTimelineEntity(baseEntityName, uid, timelineConfig, true);
    }

    private _addStickyTimelineEntity(baseEntityName: string, uid: string, timelineConfig: {
        pathToUid: string,
        pathToTitle: string,
        pathToEntity: string,
        baseEntityAlias?: string
    }, isSystemSticky: boolean): void {
        // Check if entity already exists
        if (this.stickyTimelineEntities.some(entity => entity.uid === uid)) {
            return;
        }

        if (!isSystemSticky && this.isUserStickyLimitReached()) {
            return;
        }

        // Add new entity to flat list
        const newEntity: StickyEntity = {
            uid,
            baseEntity: baseEntityName,
            timelineConfig,
            isSystemSticky
        };

        this.stickyTimelineEntities = [...this.stickyTimelineEntities, newEntity];
        this.dispatchEvent(new StateEvent(`stickyTimelineEntities`, this.stickyTimelineEntities, this));
    }

    /** Whether the user already has the maximum of five stickies; system stickies do not count. */
    public isUserStickyLimitReached(): boolean {
        return this.stickyTimelineEntities.filter(e => !e.isSystemSticky).length >= 5;
    }

    public removeStickyTimelineEntity(uid: string): void {
        this.stickyTimelineEntities = this.stickyTimelineEntities.filter(entity => entity.uid !== uid);
        this.dispatchEvent(new StateEvent(`stickyTimelineEntities`, this.stickyTimelineEntities, this));
    }

    public isStickyEntity(uid: string): boolean {
        return this.stickyTimelineEntities.some(entity => entity.uid === uid);
    }

    public isSystemStickyEntity(uid: string): boolean {
        return this.stickyTimelineEntities.some(entity =>
            entity.isSystemSticky && entity.uid === uid
        );
    }

    public isUserStickyEntity(uid: string): boolean {
        return this.stickyTimelineEntities.some(entity =>
            !entity.isSystemSticky && entity.uid === uid
        );
    }

    public getStickyEntityUids(): string[] {
        return this.stickyTimelineEntities.map(entity => entity.uid);
    }

    /**
     * Migrates legacy StickyEntityGroup data to new flat StickyEntity structure.
     * This is needed when upgrading from group-based to flat list structure.
     */
    public migrateLegacyStickyTimelineEntities(): void {
        let hasChanges = false;
        const migratedEntities: StickyEntity[] = [];
        
        for (const item of this.stickyTimelineEntities) {
            // Check if item is legacy StickyEntityGroup (has 'uids' array)
            if ((item as any).uids && Array.isArray((item as any).uids)) {
                hasChanges = true;
                const legacyGroup = item as any;
                
                // Convert each UID in group to individual StickyEntity
                for (const uid of legacyGroup.uids) {
                    migratedEntities.push({
                        uid,
                        baseEntity: legacyGroup.baseEntity,
                        timelineConfig: legacyGroup.timelineConfig,
                        isSystemSticky: legacyGroup.isSystemSticky
                    });
                }
            } else if ((item as StickyEntity).uid && (item as StickyEntity).timelineConfig) {
                // Already new format - keep as is
                migratedEntities.push(item as StickyEntity);
            }
        }

        if (hasChanges) {
            this.stickyTimelineEntities = migratedEntities;
            this.dispatchEvent(new StateEvent(`stickyTimelineEntities`, this.stickyTimelineEntities, this));
        }
    }

    /**
     * @deprecated Use migrateLegacyStickyTimelineEntities() instead
     */
    public clearLegacyStickyTimelineEntities(): void {
        this.migrateLegacyStickyTimelineEntities();
    }

    /**
     * Get Y-axis start cursor from ViewState or null for first visit.
     *
     * @param viewName Unique view name (e.g., 'boards', 'dailys')
     * @returns ShortId of the last Y-axis position or null
     */
    public getViewCursorY(viewName: string): string | null {
        return this.timelineViewCursors[viewName] ?? null;
    }

    /**
     * Set Y-axis start cursor for a view.
     * No-op if cursor value is identical (prevents unnecessary re-renders).
     *
     * @param viewName Unique view name (e.g., 'boards', 'dailys')
     * @param cursorY ShortId of the Y-axis position
     */
    public setViewCursorY(viewName: string, cursorY: string): void {
        if (this.timelineViewCursors[viewName] === cursorY) return;  // No-op if identical
        this.timelineViewCursors = {
            ...(this.timelineViewCursors || {}),
            [viewName]: cursorY
        };
    }

    /**
     * Set the persisted tasklist scroll anchor for one mode. No-op if the uid
     * is identical (prevents unnecessary re-renders) — same pattern as
     * setViewCursorY above. `uid === null` means "today" (see
     * HaTasklistView._restoreScrollAnchor()'s fallback).
     *
     * @param mode 'tasks' (/tasklist) or 'absences' (/absence-list)
     * @param uid The anchored issue's uid, or null for "today"
     */
    public setTaskListScrollAnchor(mode: 'tasks' | 'absences', uid: string | null): void {
        if (this.taskListScrollAnchor[mode] === uid) return; // No-op if identical
        this.taskListScrollAnchor = {
            ...(this.taskListScrollAnchor || {}),
            [mode]: uid
        };
    }
}

// Migrate UUID-based localStorage values to SIDs before @storage decorators read them
migrateLocalStorageToSid();
// One-time reset of the orphaned sidebarNavigationState value (0001-sidebar-restructure) —
// see sidebar-navigation-state-migration.helper.ts's doc comment.
migrateSidebarNavigationState();

export const appState = new AppState();